'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { useOptionalDesktopLayout } from '@/features/project';
import { useBreakpoint } from '@/shared/hooks/use-media-query';

interface AngleImageFrameProps {
  children: ReactNode;
  className?: string;
}

/**
 * Shared outer frame for angle image cards (main and derivative).
 *
 * - Horizontal (two-column xl+ desktop) layout: `rounded-2xl bg-settings-card`
 *   surface, with auto vertical padding (`py-2`) when the image doesn't
 *   fully fill the frame width — so the gray strips on the sides are
 *   matched by equal strips on top/bottom.
 * - Single-column layout: tight `rounded-lg bg-gray-50` frame. On narrow
 *   phones (`< sm`) the frame bleeds to the screen edges (100% width, no page
 *   inset) and drops its border — parity with Apple's full-bleed stacked image
 *   header. Tablet-portrait single-column (`sm`+) keeps the inset, bordered
 *   frame. Only the image frame bleeds; sibling note/feedback banners stay
 *   inset.
 *
 * The inner image element must carry the `data-image-frame` attribute so
 * the ResizeObserver here can detect whether side strips are present.
 */
export function AngleImageFrame({ children, className }: AngleImageFrameProps) {
  const desktopLayout = useOptionalDesktopLayout();
  const { isXl } = useBreakpoint();
  const isHorizontalLayout = !!(isXl && desktopLayout);

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Default to true so the frame doesn't pop in after image load.
  const [needsPadding, setNeedsPadding] = useState(true);

  useEffect(() => {
    if (!isHorizontalLayout) {
      setNeedsPadding(false);
      return;
    }
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const measure = () => {
      const frame = wrapper.querySelector(
        '[data-image-frame]'
      ) as HTMLElement | null;
      if (!frame) return;
      const wrapperW = wrapper.clientWidth;
      const frameW = frame.getBoundingClientRect().width;
      setNeedsPadding(wrapperW - frameW > 0.5);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [isHorizontalLayout]);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'relative overflow-hidden',
        // Narrow phones (< sm): square the whole card. The image rides full-bleed
        // to the screen edges, where rounded corners read as a mistake. Squares
        // the inner image container too (`[data-image-frame]`, which carries its
        // own `rounded-lg`) so no frame-gray peeks in the rounded corners.
        // `sm`+ (tablet portrait / desktop) keeps the rounded card.
        'max-sm:[&_[data-image-frame]]:rounded-none',
        isHorizontalLayout
          ? 'rounded-2xl bg-settings-card'
          : // Narrow phones bleed to the screen edges (no page inset) and drop
            // the side border so no hairline sits flush against the edge;
            // `sm`+ (tablet portrait) restores the inset + bordered frame.
            'rounded-none sm:rounded-lg bg-gray-50 dark:bg-gray-900 -mx-4 sm:mx-0 sm:border sm:border-border',
        isHorizontalLayout && needsPadding && 'py-2',
        className
      )}
    >
      {children}
    </div>
  );
}
